import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';

import { AuthProvider } from '@/contexts/AuthContext';
import { Toaster } from '@/components/ui/toaster';
import { MainLayout } from '@/components/layout/MainLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';

// Pages
import { LoginPage } from '@/pages/LoginPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { PrisonerListPage } from '@/pages/PrisonerListPage';
import { NewPrisonerPage } from '@/pages/NewPrisonerPage';
import { PrisonerDetailPage } from '@/pages/PrisonerDetailPage';
import { SearchPage } from '@/pages/SearchPage';
import { AdminPage } from '@/pages/admin/AdminPage';
import { AuditLogPage } from '@/pages/admin/AuditLogPage';
import { UsersPage } from '@/pages/admin/UsersPage';
import { ManagementDashboardPage } from '@/pages/ManagementDashboardPage';
import { VgoWorkflowsPage } from '@/pages/VgoWorkflowsPage';
import { NewVgoWorkflowPage } from '@/pages/NewVgoWorkflowPage';
import { VgoWorkflowDetailPage } from '@/pages/VgoWorkflowDetailPage';
import { AiAssistantPage } from '@/pages/AiAssistantPage';
import { AiSettingsPage } from '@/pages/admin/AiSettingsPage';
import { UnauthorizedPage } from '@/pages/UnauthorizedPage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            
            <Route element={<ProtectedRoute />}>
              <Route element={<MainLayout />}>
                <Route path="/" element={<Navigate to="/dashboard" replace />} />
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route
                  path="/dashboard/management"
                  element={
                    <ProtectedRoute requiredPermissions={['menu.management-dashboard.view']}>
                      <ManagementDashboardPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/prisoners" element={<PrisonerListPage />} />
                <Route path="/prisoners/new" element={<NewPrisonerPage />} />
                <Route path="/prisoners/:id" element={<PrisonerDetailPage />} />
                <Route path="/search" element={<SearchPage />} />
                <Route
                  path="/ai-assistant"
                  element={
                    <ProtectedRoute requiredPermissions={['ai.assistant.use']}>
                      <AiAssistantPage />
                    </ProtectedRoute>
                  }
                />
                <Route path="/vgo-workflows" element={<VgoWorkflowsPage />} />
                <Route path="/vgo-workflows/new" element={<NewVgoWorkflowPage />} />
                <Route path="/vgo-workflows/:id" element={<VgoWorkflowDetailPage />} />
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute requiredPermissions={['menu.admin.view']}>
                      <AdminPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute requiredPermissions={['admin.users.view']}>
                      <UsersPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/audit"
                  element={
                    <ProtectedRoute requiredPermissions={['admin.audit.view']}>
                      <AuditLogPage />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/ai-settings"
                  element={
                    <ProtectedRoute requiredPermissions={['admin.ai-settings.view']}>
                      <AiSettingsPage />
                    </ProtectedRoute>
                  }
                />
              </Route>
            </Route>
          </Routes>
          <Toaster />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
